if (!customElements.get('about-scroll-story')) {
  customElements.define('about-scroll-story', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.track = this.querySelector('[data-reel-track]');
      this.frames = [...this.track.children];
      this.stage = this.querySelector('.about-collage__stage');
      this.copy = this.querySelector('.about-collage__copy');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      const { signal } = this.abort;
      const schedule = () => {
        cancelAnimationFrame(this.storyFrame);
        this.storyFrame = requestAnimationFrame(() => this.render());
      };
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', () => this.measure(), { passive: true, signal });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.frames.length - 1 : this.currentIndex() + (event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1);
        this.show(index);
      }, { signal });
      this.motion.addEventListener('change', () => this.measure(), { signal });
      this.track.addEventListener('focusin', (event) => {
        const index = this.frames.findIndex((frame) => frame.contains(event.target));
        if (this.active && index >= 0) this.show(index);
      }, { signal });
      this.storyResize = new ResizeObserver(() => this.measure());
      this.storyResize.observe(this.stage);
      this.storyResize.observe(this.copy);
      this.measure();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.storyResize?.disconnect();
      cancelAnimationFrame(this.storyFrame);
    }
    ease(progress) {
      const p = Math.max(0, Math.min(1, progress));
      const edge = .15;
      if (p < edge) return p * p / (2 * edge * (1 - edge));
      if (p > 1 - edge) return 1 - (1 - p) ** 2 / (2 * edge * (1 - edge));
      return (p - edge / 2) / (1 - edge);
    }
    inverse(progress) {
      let low = 0, high = 1;
      for (let i = 0; i < 24; i++) {
        const middle = (low + high) / 2;
        if (this.ease(middle) < progress) low = middle;
        else high = middle;
      }
      return (low + high) / 2;
    }
    measure() {
      this.active = !this.motion.matches && this.frames.length > 1;
      this.toggleAttribute('data-scroll-story-active', this.active);
      if (this.active) {
        const style = getComputedStyle(this.stage);
        const available = this.stage.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
        const mobile = matchMedia('(max-width: 699px)').matches;
        const photoHeight = available - (mobile ? this.copy.offsetHeight + parseFloat(style.rowGap) : 0);
        if (photoHeight < 160 || (!mobile && this.copy.offsetHeight > available)) this.active = false;
        else this.stage.style.setProperty('--story-photo-height', `${photoHeight}px`);
      }
      this.toggleAttribute('data-scroll-story-active', this.active);
      this.distance = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      if (this.distance < 2) {
        this.active = false;
        this.removeAttribute('data-scroll-story-active');
      }
      this.style.height = this.active ? `${this.stage.offsetHeight + this.distance}px` : '';
      this.render();
    }
    render() {
      if (!this.active) return;
      const top = parseFloat(getComputedStyle(this.stage).top) || 0;
      const progress = (top - this.getBoundingClientRect().top) / this.distance;
      const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      this.track.scrollLeft = direction * this.distance * this.ease(progress);

    }
    currentIndex() {
      if (Math.abs(this.track.scrollLeft) >= this.track.scrollWidth - this.track.clientWidth - 3) return this.frames.length - 1;
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const padding = parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
      const bounds = this.track.getBoundingClientRect();
      let nearest = 0, distance = Infinity;
      this.frames.forEach((frame, index) => {
        const rect = frame.getBoundingClientRect();
        const delta = Math.abs(rtl ? rect.right - bounds.right + padding : rect.left - bounds.left - padding);
        if (delta < distance) { nearest = index; distance = delta; }
      });
      return nearest;
    }
    show(index) {
      const frame = this.frames[Math.max(0, Math.min(index, this.frames.length - 1))];
      if (!frame) return;
      const style = getComputedStyle(this.track);
      const rtl = style.direction === 'rtl';
      const padding = parseFloat(style.scrollPaddingInlineStart) || 0;
      const bounds = frame.getBoundingClientRect();
      const trackBounds = this.track.getBoundingClientRect();
      const delta = rtl ? trackBounds.right - bounds.right - padding : bounds.left - trackBounds.left - padding;
      if (!this.active) {
        this.track.scrollBy({ left: delta * (rtl ? -1 : 1), behavior: this.motion.matches ? 'instant' : 'smooth' });
        return;
      }
      const offset = Math.max(0, Math.min(this.distance, Math.abs(this.track.scrollLeft) + delta));
      const top = parseFloat(getComputedStyle(this.stage).top) || 0;
      const start = window.scrollY + this.getBoundingClientRect().top - top;
      window.scrollTo({ top: start + this.inverse(offset / this.distance) * this.distance, behavior: 'smooth' });
    }
  });
}
